import { HodAutocomplete as Autocomplete } from "@/src/lib/ui";
import TextField from "@/src/components/custom-fields/TextField";
import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateHookCity } from "@/src/lib/store/hook-city/slice";
import {
    manageLoggedOutUserSession,
    updateSession,
} from "@/src/lib/util/updateSession";
import useAuth from "@/src/lib/hooks/useAuth";
import { getLocationFromApi } from "@/src/lib/util/location";
import Iconify from "@/src/components/iconify";
import IconCode from "@/src/lib/iconCode";
import { useToast } from "@/src/lib/hooks/useToast";

const getGpsCoords = () => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error("Geolocation is not supported"));
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                });
            },
            (error) => reject(error)
        );
    });
};

const getNearestCity = (cityLists, lat, lng) => {
    const toRad = (v) => (v * Math.PI) / 180;
    return (
        cityLists.reduce((nearest, c) => {
            const cLat = c?.avgPosition?.lat;
            const cLng = c?.avgPosition?.long;
            if (cLat == null || cLng == null) return nearest;
            const dLat = toRad(cLat - lat);
            const dLng = toRad(cLng - lng);
            const a =
                Math.sin(dLat / 2) ** 2 +
                Math.cos(toRad(lat)) *
                    Math.cos(toRad(cLat)) *
                    Math.sin(dLng / 2) ** 2;
            const dist = Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            return !nearest || dist < nearest.dist ? { c, dist } : nearest;
        }, null)?.c || null
    );
};

const CityList = ({ cityLists }) => {
    const { isLoggedIn } = useAuth();
    const userDetails = useSelector((state) => state?.user);
    const currentCentreDetails = userDetails?.userInfo?.centreDetails;
    const { cityId } = currentCentreDetails || {};
    const HOOKCITYID = useSelector((state) => state?.hookCity?.cityId);
    const dispatch = useDispatch();
    const { showCustomToast } = useToast();
    const [city, setCity] = useState(null);
    const [isLocating, setIsLocating] = useState(false);
    const autoDetectedRef = useRef(false);

    useEffect(() => {
        if (cityLists?.length > 0) {
            // Logged-in: prefer their assigned centre city; logged-out: always follow HOOKCITYID
            const selectedCityId = isLoggedIn ? (cityId || HOOKCITYID) : HOOKCITYID;
            const defaultCity = cityLists?.find(
                (city) => String(city.cityId) === String(selectedCityId)
            );

            setCity(defaultCity || null); // Set the default city or null
        }
    }, [cityLists, cityId, currentCentreDetails, HOOKCITYID, isLoggedIn]);

    // Auto-detect city from coordinates on load, only when no city is set yet
    useEffect(() => {
        if (!cityLists?.length) return;
        if (cityId || HOOKCITYID) return;
        if (autoDetectedRef.current) return;
        autoDetectedRef.current = true;

        let isMounted = true;

        const autoDetect = async () => {
            let ipNearest = null;
            try {
                const pos = await getLocationFromApi();
                ipNearest = getNearestCity(cityLists, pos?.lat, pos?.lng);
                if (ipNearest && isMounted) {
                    setCity(ipNearest);
                    await updateHookCityHandler(null, ipNearest);
                }
            } catch (error) {
                console.error("Error auto-detecting city from IP:", error.message);
            }

            try {
                const pos = await getGpsCoords();
                const gpsNearest = getNearestCity(cityLists, pos?.lat, pos?.lng);
                if (
                    gpsNearest &&
                    isMounted &&
                    gpsNearest.cityId !== ipNearest?.cityId
                ) {
                    setCity(gpsNearest);
                    await updateHookCityHandler(null, gpsNearest);
                }
            } catch (error) {
                // GPS denied/unavailable — keep the IP-based city as-is
                console.error(
                    "GPS location not used:",
                    error?.code,
                    error?.message
                );
            }
        };
        autoDetect();

        return () => {
            isMounted = false;
        };
    }, [cityLists,cityId, HOOKCITYID]);

    const updateHookCityHandler = async (event, value) => {
        try {
            dispatch(updateHookCity(value));

            const sessionUpdateType = "HOOKCITY";
            const updateData = { hookCityId: value?.cityId };
            const sessionHandler = isLoggedIn
                ? updateSession
                : manageLoggedOutUserSession;
            await sessionHandler(sessionUpdateType, updateData, dispatch);
        } catch (error) {
            console.error("Error during session update:", error.message);
        }
        // useSessionUpdateHandler({ hookCityId: value?.cityId });
    };

    const handleUseCurrentLocation = async (inputEl) => {
        // hodAutocomplete.js already closes the dropdown on Escape keydown
        inputEl?.dispatchEvent(
            new KeyboardEvent("keydown", {
                key: "Escape",
                bubbles: true,
                cancelable: true,
            })
        );
        if (!cityLists?.length || isLocating) return;
        setIsLocating(true);
        try {
            const pos = await getGpsCoords();
            const nearest = getNearestCity(cityLists, pos?.lat, pos?.lng);
            if (nearest && nearest.cityId !== city?.cityId) {
                setCity(nearest);
                await updateHookCityHandler(null, nearest);
            }
        } catch (error) {
            showCustomToast(
                "Unable to get your location. Please allow location access and try again.",
                "error"
            );
        } finally {
            setIsLocating(false);
        }
    };

    return (
        <Autocomplete
            className="w-full"
            value={city}
            size="small"
            options={cityLists || []}
            getOptionLabel={(option) => option.cityName || ""}
            isOptionEqualToValue={(option, value) =>
                option.cityId === value.cityId
            }
            onChange={(event, newValue) =>
                updateHookCityHandler(event, newValue)
            }
            disableClearable={true}
            renderInput={(params) => (
                <TextField
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        className: "ctf-no-border",
                        endAdornment: (
                            <span
                                onMouseDown={(e) => {
                                    // Handle at mousedown to prevent root onMouseDown from firing
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                onClick={() =>
                                    handleUseCurrentLocation(
                                        params.inputProps.ref?.current
                                    )
                                }
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        handleUseCurrentLocation(
                                            params.inputProps.ref?.current
                                        );
                                    }
                                }}
                                style={{
                                    opacity: isLocating ? 0.4 : 0.6,
                                    paddingRight: 6,
                                    cursor: isLocating
                                        ? "default"
                                        : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                role="button"
                                tabIndex={0}
                                aria-label="Use current location"
                            >
                                <Iconify icon={IconCode.locate} width={18} />
                            </span>
                        ),
                    }}
                    id="city-autocomplete"
                    placeholder="Select City"
                  //  label="City"
                    aria-label="Select City" // ✅ Extra for accessibility
                />
            )}
        />
    );
};
export default CityList;

// import React from 'react';
// import updateSession from './path/to/updateSession';

// function YourComponent() {
//     const update = updateSession();

//     const handleUpdateSession = async () => {
//         try {
//             const sessionUpdateType = 'ADDRESS'; // or 'CENTRE', 'CART', 'HOOKCITY'
//             const updateData = {
//                 pincode: '123456',
//                 addressGuid: 'your-address-guid',
//             };

//             const response = await update(sessionUpdateType, updateData);
//             console.log('Session updated successfully:', response);
//         } catch (error) {
//             console.error('Error during session update:', error.message);
//         }
//     };

//     return (
//         <div>
//             <button onClick={handleUpdateSession}>
//                 Update Session
//             </button>
//         </div>
//     );
// }

// export default YourComponent;
